import React, { useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { Download } from '@mui/icons-material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { CustomerTypes } from '../../../../../types';
import { useGenericQuery } from '../../../../../services/react-query/generic.query';
import { downloadCsv } from '../../../../../hooks/useCsvDownload';

dayjs.extend(relativeTime);

const ROWS_PER_PAGE = 25;

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

const STATUS_META: Record<CustomerTypes.RapidSosRegistryStatus, { label: string; color: ChipColor }> = {
    active: { label: 'Active', color: 'success' },
    dormant: { label: 'Dormant > 30d', color: 'warning' },
    'user-not-found': { label: 'User not found', color: 'error' },
};

// The uri scheme ("client-id://...") is what this maps to; the API's
// `uriType` discriminator is named "user-id" internally.
const formatUriType = (uriType: CustomerTypes.RapidSosRegistryUriType): string =>
    uriType === 'user-id' ? 'client-id' : 'phone';

interface Props {
    customerId?: string;
    customerName?: string;
    // Gates the fetch — this tab's data is only loaded while it's selected.
    active: boolean;
}

export const RapidSosRegistryTab: React.FC<Props> = ({ customerId, customerName, active }) => {
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);

    const { data, isLoading, isError } = useGenericQuery<CustomerTypes.RapidSosRegistryResponse>(
        'getRapidSosRegistry',
        `customer/${customerId}/rapidsos-registry`,
        undefined,
        undefined,
        false,
        undefined,
        active && !!customerId,
    );

    const items = data?.items ?? [];
    const summary = data?.summary;

    const filteredItems = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return items;
        return items.filter((item) =>
            (item.name || '').toLowerCase().includes(term) ||
            (item.phoneNumber || '').toLowerCase().includes(term) ||
            (item.employeeId || '').toLowerCase().includes(term)
        );
    }, [items, search]);

    const pagedItems = filteredItems.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);

    const handleSearchChange = (value: string) => {
        setSearch(value);
        setPage(0);
    };

    // Exports every row regardless of the current search/page — the table
    // itself only ever shows a filtered, paginated slice.
    const handleExportCsv = () => {
        const rows = items.map((item) => ({
            name: item.name || '',
            phoneNumber: item.phoneNumber || '',
            employeeId: item.employeeId || '',
            status: STATUS_META[item.status]?.label ?? item.status,
            lastActivityAt: item.lastActivityAt
                ? dayjs(item.lastActivityAt).format('YYYY-MM-DD HH:mm')
                : 'Never',
            registeredAt: item.registeredAt
                ? `${item.registeredAtIsEstimate ? '~' : ''}${dayjs(item.registeredAt).format('YYYY-MM-DD')}`
                : 'Unknown',
            registryId: item.uri,
            userId: item.userId,
            subscriptionId: item.subscriptionId,
        }));

        const today = dayjs().format('YYYY-MM-DD');
        downloadCsv(rows, `rapidsos-registry-${customerName || customerId}-${today}`);
    };

    return (
        <Card sx={{ mt: 2 }}>
            <CardContent>
                {isLoading && (
                    <Stack alignItems="center" py={6}>
                        <CircularProgress />
                    </Stack>
                )}

                {!isLoading && isError && (
                    <Alert severity="error">
                        Could not load the RapidSOS registry. Refresh the page to try again.
                    </Alert>
                )}

                {!isLoading && !isError && (
                    <>
                        {/* Stat tiles */}
                        <Grid container spacing={2} mb={1}>
                            <Grid item xs={12} md={4}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="body2" color="text.secondary">
                                            Registered users (billed)
                                        </Typography>
                                        <Typography variant="h4" data-testid="rapidsos-stat-registered">
                                            {summary?.registered ?? 0}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="body2" color="text.secondary">
                                            Estimated monthly cost
                                        </Typography>
                                        <Typography variant="h4" data-testid="rapidsos-stat-cost">
                                            ${(summary?.estimatedMonthlyCostUsd ?? 0).toFixed(2)}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        borderColor: (summary?.userNotFound ?? 0) > 0 ? 'error.main' : undefined,
                                    }}
                                >
                                    <CardContent>
                                        <Typography variant="body2" color="text.secondary">
                                            Users not found
                                        </Typography>
                                        <Typography
                                            variant="h4"
                                            color={(summary?.userNotFound ?? 0) > 0 ? 'error.main' : 'text.primary'}
                                            data-testid="rapidsos-stat-user-not-found"
                                        >
                                            {summary?.userNotFound ?? 0}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                            Mirror of the RapidSOS registry. Updated on each registration change and
                            reconciled with RapidSOS monthly.
                        </Typography>

                        {items.length === 0 ? (
                            <Typography color="text.secondary" py={4}>
                                No users of this customer are registered with RapidSOS.
                            </Typography>
                        ) : (
                            <>
                                <Grid container spacing={2} alignItems="center" mb={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Search"
                                            placeholder="Search by name, phone, or employee ID..."
                                            value={search}
                                            onChange={(e) => handleSearchChange(e.target.value)}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6} display="flex" justifyContent="flex-end">
                                        <Button
                                            variant="outlined"
                                            startIcon={<Download />}
                                            onClick={handleExportCsv}
                                        >
                                            Export CSV
                                        </Button>
                                    </Grid>
                                </Grid>

                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                                                        Name
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                                                        Phone
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                                                        Employee ID
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                                                        Status
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                                                        Last activity
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                                                        Registered
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                                                        Registry ID
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {pagedItems.map((item) => {
                                                const statusMeta =
                                                    STATUS_META[item.status] ??
                                                    ({ label: item.status, color: 'default' } as { label: string; color: ChipColor });

                                                return (
                                                    <TableRow key={`${item.userId}-${item.uri}`}>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                                                                {item.name || 'N/A'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                                {item.phoneNumber || 'N/A'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                                {item.employeeId || 'N/A'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip label={statusMeta.label} color={statusMeta.color} size="small" />
                                                        </TableCell>
                                                        <TableCell>
                                                            {item.lastActivityAt ? (
                                                                <Tooltip title={dayjs(item.lastActivityAt).format('LLL')}>
                                                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                                        {dayjs(item.lastActivityAt).fromNow()}
                                                                    </Typography>
                                                                </Tooltip>
                                                            ) : (
                                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                                    Never
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {!item.registeredAt ? (
                                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                                    Unknown
                                                                </Typography>
                                                            ) : item.registeredAtIsEstimate ? (
                                                                <Tooltip title="estimated">
                                                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                                        ~{dayjs(item.registeredAt).format('MM/DD/YYYY')}
                                                                    </Typography>
                                                                </Tooltip>
                                                            ) : (
                                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                                    {dayjs(item.registeredAt).format('MM/DD/YYYY')}
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Tooltip title={item.uri}>
                                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                                    {formatUriType(item.uriType)}
                                                                </Typography>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <TablePagination
                                    rowsPerPageOptions={[ROWS_PER_PAGE]}
                                    component="div"
                                    count={filteredItems.length}
                                    rowsPerPage={ROWS_PER_PAGE}
                                    page={page}
                                    onPageChange={(_, p) => setPage(p)}
                                />
                            </>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
};
